import { useCampaignApproval } from 'hooks/useCampaignApproval'
import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { EuiButton, EuiCallOut, EuiFlexGroup, EuiFlexItem, EuiSpacer } from '@elastic/eui'

import { CampaignStatus } from 'api/interfaces'
import { useApproveCampaignMutation } from 'api/rtkQueryApi/platform/campaignsApi'
import { showErrorToast, showSuccessToast } from 'app/appSlice'
import { RootState } from 'app/rootReducer'
import { fetchCampaigns } from 'features/campaigns/campaigns/campaignsSlice'

import { ActivateCampaignContent } from './ActivateCampaignContent'
import { ApproveWithUnsavedChangesModal } from './ApproveWithUnsavedChangesModal'
import { useCampaignDetailsEdit } from './CampaignDetailsEditContext'
import { DisapproveCampaignModal } from './DisapproveCampaignModal'
import { fetchCampaign } from './campaignSlice'

interface ActivatePageProps {
  campaignId: string
  amount: number
  campaignType: string
  budgetFrequency: string
}

const ActivateCallOut = ({ campaignId, amount, campaignType }: ActivatePageProps) => {
  const dispatch = useDispatch()
  const { currentAccount } = useSelector((state: RootState) => state.app)
  const { campaign } = useSelector((state: RootState) => state.campaign)
  const { isEnabled: campaignApprovalEnabled, canApprove } = useCampaignApproval()
  const [approveCampaign, approveCampaignResult] = useApproveCampaignMutation()
  const [showDisapproveModal, setShowDisapproveModal] = useState(false)
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false)
  const [isSavingChanges, setIsSavingChanges] = useState(false)
  const detailsEdit = useCampaignDetailsEdit()

  const isPendingApproval = campaignApprovalEnabled && campaign?.status === CampaignStatus.PendingApproval
  const isApproved = campaignApprovalEnabled && campaign?.status === CampaignStatus.SavedForLater

  const refreshCampaign = () => {
    if (!currentAccount) return
    dispatch(fetchCampaign(currentAccount.id, campaignId))
    dispatch(fetchCampaigns(currentAccount.id))
  }

  const onApprove = async () => {
    if (!currentAccount) return
    try {
      const result = await approveCampaign({ accountId: currentAccount.id, campaignId }).unwrap()
      dispatch(showSuccessToast(result.activated ? 'Campaign approved and activated' : 'Campaign approved'))
      refreshCampaign()
    } catch {
      dispatch(showErrorToast('Failed to approve campaign'))
      refreshCampaign()
    }
  }

  const onApproveClick = () => {
    if (detailsEdit.hasUnsavedChanges) {
      setShowUnsavedChangesModal(true)
      return
    }
    onApprove()
  }

  const onCancelUnsavedChanges = () => {
    if (isSavingChanges) return
    setShowUnsavedChangesModal(false)
  }

  const onApproveWithoutSaving = () => {
    detailsEdit.discardChanges()
    setShowUnsavedChangesModal(false)
    onApprove()
  }

  const onSaveAndApprove = async () => {
    setIsSavingChanges(true)
    try {
      await detailsEdit.saveChanges()
    } catch {
      dispatch(showErrorToast('Failed to save campaign changes'))
      setIsSavingChanges(false)
      return
    }
    setIsSavingChanges(false)
    setShowUnsavedChangesModal(false)
    await onApprove()
  }

  if (isPendingApproval && !canApprove) {
    return <EuiCallOut title='Awaiting approval' iconType='clock' color='warning' />
  }

  if (isPendingApproval && canApprove) {
    return (
      <React.Fragment>
        <EuiCallOut title='This campaign is awaiting your approval.' iconType='play' color='success'>
          <EuiFlexGroup>
            <EuiFlexItem grow={false}>
              <EuiButton fill onClick={onApproveClick} isLoading={approveCampaignResult.isLoading}>
                Approve
              </EuiButton>
            </EuiFlexItem>
            <EuiFlexItem grow={false}>
              <EuiButton color='danger' onClick={() => setShowDisapproveModal(true)}>
                Disapprove
              </EuiButton>
            </EuiFlexItem>
          </EuiFlexGroup>
          {approveCampaignResult.data?.requiresPaymentActivation && (
            <React.Fragment>
              <EuiSpacer />
              <ActivateCampaignContent campaignId={campaignId} campaignType={campaignType} amount={amount} />
            </React.Fragment>
          )}
        </EuiCallOut>
        {showDisapproveModal && currentAccount && <DisapproveCampaignModal accountId={currentAccount.id} campaignId={campaignId} onClose={() => setShowDisapproveModal(false)} onDisapproved={refreshCampaign} />}
        {showUnsavedChangesModal && <ApproveWithUnsavedChangesModal canSave={detailsEdit.canSaveChanges} isSaving={isSavingChanges} onCancel={onCancelUnsavedChanges} onApproveWithoutSaving={onApproveWithoutSaving} onSaveAndApprove={onSaveAndApprove} />}
      </React.Fragment>
    )
  }

  if (campaignApprovalEnabled && !canApprove && isApproved) {
    return null
  }

  return (
    <React.Fragment>
      <EuiCallOut title='This campaign still needs to be activated before it will run.' iconType='play' color='success'>
        <ActivateCampaignContent campaignId={campaignId} campaignType={campaignType} amount={amount} />
      </EuiCallOut>
    </React.Fragment>
  )
}

export default ActivateCallOut
